import { useState } from "react";
import { useNavigate } from "react-router-dom";

import Breadcrumb from "@/components/common/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { ArrowLeft, Edit, Plus, Trash } from "lucide-react";
import { TaskLabel } from "@/types/task-management";
import { mockTaskLabels } from "@/lib/mock/task-management";
import { v4 as uuidv4 } from "uuid";

export default function TaskLabelsPage() {
  const navigate = useNavigate();
  const [labels, setLabels] = useState<TaskLabel[]>(mockTaskLabels);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentLabel, setCurrentLabel] = useState<TaskLabel | null>(null);
  const [labelName, setLabelName] = useState("");
  const [labelColor, setLabelColor] = useState("#3b82f6");
  
  const handleOpenDialog = (label?: TaskLabel) => {
    if (label) {
      setCurrentLabel(label);
      setLabelName(label.name);
      setLabelColor(label.color);
    } else {
      setCurrentLabel(null);
      setLabelName("");
      setLabelColor("#3b82f6");
    }
    setIsDialogOpen(true);
  };
  
  const handleSaveLabel = () => {
    if (labelName.trim() === "") return;
    
    if (currentLabel) {
      // Editar etiqueta existente
      setLabels(labels.map(label => 
        label.id === currentLabel.id 
          ? { ...label, name: labelName, color: labelColor } 
          : label
      ));
    } else {
      // Criar nova etiqueta
      const newLabel: TaskLabel = {
        id: uuidv4(),
        name: labelName,
        color: labelColor
      };
      setLabels([...labels, newLabel]);
    }
    
    setIsDialogOpen(false);
  };
  
  const handleDeleteLabel = (id: string) => {
    if (confirm("Tem certeza que deseja excluir esta etiqueta?")) {
      setLabels(labels.filter(label => label.id !== id));
    }
  };
  
  return (
    <div className="container mx-auto py-6 space-y-6">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb 
            items={[
              { label: "Dashboard", href: "/" },
              { label: "Minhas Atividades", href: "/task-management" },
              { label: "Gerenciar Etiquetas" }
            ]} 
          />
        </div>
        
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/task-management")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-extrabold">Gerenciar Etiquetas</h1>
            <p className="text-muted-foreground">
              Crie e edite etiquetas para organizar suas tarefas
            </p>
          </div>
        </div>
        
        <div className="flex justify-end mb-4">
          <Button onClick={() => handleOpenDialog()} className="gap-1">
            <Plus className="h-4 w-4" />
            <span>Nova Etiqueta</span>
          </Button>
        </div>
        
        <div className="bg-card rounded-lg border shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Cor</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {labels.length > 0 ? (
                labels.map(label => (
                  <TableRow key={label.id}>
                    <TableCell>
                      <div 
                        className="w-6 h-6 rounded-full" 
                        style={{ backgroundColor: label.color }}
                      />
                    </TableCell>
                    <TableCell className="font-extrabold">{label.name}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleOpenDialog(label)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteLabel(label.id)}
                        >
                          <Trash className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                    Nenhuma etiqueta encontrada. Crie uma nova etiqueta para começar.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Diálogo para criar/editar etiqueta */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {currentLabel ? "Editar Etiqueta" : "Nova Etiqueta"}
              </DialogTitle>
              <DialogDescription>
                {currentLabel 
                  ? "Edite os detalhes da etiqueta selecionada." 
                  : "Crie uma nova etiqueta para organizar suas tarefas."}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="label-name">Nome da etiqueta</Label>
                <Input
                  id="label-name"
                  value={labelName}
                  onChange={(e) => setLabelName(e.target.value)}
                  placeholder="Ex: Trabalho, Pessoal, Urgente"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="label-color">Cor</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="label-color"
                    type="color"
                    value={labelColor}
                    onChange={(e) => setLabelColor(e.target.value)}
                    className="w-12 h-8 p-1"
                  />
                  <div className="flex-1">
                    <Input
                      value={labelColor}
                      onChange={(e) => setLabelColor(e.target.value)}
                      placeholder="#3b82f6"
                    />
                  </div>
                </div>
              </div>
              
              <div className="mt-2">
                <Label>Pré-visualização</Label>
                <div className="mt-2 p-2 border rounded-md">
                  <div 
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-extrabold"
                    style={{ 
                      backgroundColor: `${labelColor}20`, 
                      color: labelColor,
                      borderColor: labelColor,
                      border: '1px solid'
                    }}
                  >
                    {labelName || "Nome da etiqueta"}
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveLabel}>
                {currentLabel ? "Salvar alterações" : "Criar etiqueta"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );
} 